import { Group, Stack } from '@mantine/core';
import { PortalButton } from 'components/Button/PortalButton';
import { CreateRuleFormProvider, useCreateRuleForm } from 'context/FormContext';
import { useDrawerContext } from 'context/hooks';
import { useChangeStatusRule } from 'hooks/api/rule/useChangeStatusRule';
import { RuleItem, RuleTransitions } from 'models/rules';
import { useTranslation } from 'react-i18next';
import { BsCheck2, BsPencil } from 'react-icons/bs';
import { modals } from '@mantine/modals';
import { createConfirmApproveRuleModal, createConfirmResetRuleModal } from 'components/Modal/ConfirmModals';
import { useDefineRuleFormValidations } from './DefineRule/hooks/useDefineRuleFormValidations';
import { BasicInfoFields } from './DefineRule/Fields/BasicInfoFields';
import { RuleConfigurationFields } from './DefineRule/Fields/RuleConfigurationFields';
import { DefineConditionsFields } from './DefineRule/Fields/DefineConditionsFields';

interface Props {
  item: RuleItem;
}

export const ReviewRuleForm = ({ item }: Props) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { setFormValues } = useDefineRuleFormValidations();
  const form = useCreateRuleForm({
    initialValues: setFormValues({ item, formDisabled: true }),
  });

  const { mutate: changeStatusRule } = useChangeStatusRule({
    reset: () => form.setFieldValue('formActionsDisabled', false),
  });

  const { values } = form;

  const handleApproveRule = () => {
    form.setFieldValue('formActionsDisabled', true);
    changeStatusRule({ item, transition: RuleTransitions.approve });
  };

  const handleResetRule = () => {
    form.setFieldValue('formActionsDisabled', true);
    changeStatusRule({ item, transition: RuleTransitions.reset });
  };

  return (
    <CreateRuleFormProvider form={form}>
      <Stack>
        <BasicInfoFields />
        <RuleConfigurationFields />
        <DefineConditionsFields />
        <Group mt={'xl'} justify={'flex-start'}>
          <PortalButton
            label={t('approve_label')}
            Icon={BsCheck2}
            disabled={values.formActionsDisabled}
            onClick={() => modals.openConfirmModal(createConfirmApproveRuleModal(handleApproveRule))}
          />
          <PortalButton
            label={t('edit_label')}
            Icon={BsPencil}
            disabled={values.formActionsDisabled}
            variant={'light'}
            onClick={() => modals.openConfirmModal(createConfirmResetRuleModal(handleResetRule, item.status))}
          />
          <PortalButton
            variant={'outline'}
            onClick={() => closeDrawer()}
            label={t('cancel_label')}
            disabled={values.formActionsDisabled}
          />
        </Group>
      </Stack>
    </CreateRuleFormProvider>
  );
};
